import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  Inject,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild
} from '@angular/core';
import {SpinnerService} from '../../services/spinner.service';
import {NotificationService} from '../../notification/notification.service';
import {BundleService} from '../../services/bundle.service';
import {catchError, debounceTime, fromEvent, merge, Subject, takeUntil} from 'rxjs';
import {OrderComponent} from '../../order/order.component';
import {MatDialog} from '@angular/material/dialog';
import {isPlatformBrowser, NgClass, NgOptimizedImage} from '@angular/common';
import {TypeOrderEnum} from '../../enum/type-order.enum';
import {MatIcon} from '@angular/material/icon';
import {TenantService} from '../../services/tenant.service';
import {Router} from '@angular/router';

@Component({
  selector: 'app-menus',
  templateUrl: './menus.component.html',
  styleUrl: './menus.component.scss',
  imports: [MatIcon, NgClass, NgOptimizedImage],
  standalone: true
})
export class MenusComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  private tenantService = inject(TenantService);
  isMilano = false;
  private spinnerService = inject(SpinnerService);
  private notificationService = inject(NotificationService);
  private bundleService = inject(BundleService);
  private dialog = inject(MatDialog);
  private ngZone = inject(NgZone);
  @Inject(PLATFORM_ID) private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  FORTY_DAYS_MS = 40 * 24 * 60 * 60 * 1000;

  data = signal<any[] | null>(null);
  hasOverflow = false;   // vrai s’il y a du scroll horizontal
  showSeeMore = false;   // affiche le bouton "Lire plus" seulement si besoin
  canScrollLeft = false;
  canScrollRight = true;
  private checkScheduled = false; // pour éviter trop d’appel
  private isScrollListenerAttached = false;
  private destroy$ = new Subject<void>();
  private resizeObserver: ResizeObserver | null = null;
  private now = signal(Date.now());

  backgroundImages = [
    '/assets/images/background-bundle-1.webp',  // image orange
    '/assets/images/background-bundle-2.webp',  // image jaune
  ];
  buttonClasses = ['bg-[#B74C2B]', 'bg-[#FFB936]',];
  textClasses = ['#B74C2B', '#FFB936'];

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      setInterval(() => this.now.set(Date.now()), 60_000);
    }
    effect(() => {
      const items = this.data()
      if (items && isPlatformBrowser(this.platformId)) {
        queueMicrotask(() => {
          this.setupLayoutListeners()
        })
      }
    })
  }

  ngOnInit() {
    this.isMilano = this.tenantService.getActiveTenant() === 'milano';
    this.getBundle();

  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.setupLayoutListeners()
    }
  }


  getBundle() {
    this.spinnerService.show();
    this.bundleService.getAllBundle().pipe(
      catchError((err) => {
        this.notificationService.errorHandler(err);
        this.spinnerService.hide();
        return [];
      }),
      takeUntil(this.destroy$)
    ).subscribe((result: any) => {
      this.spinnerService.hide();
      if (result?.body?.data?.items) {
        const items = this.enrichAndFilterBundles(result.body.data.items);
        items.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        this.data.set(items);
      }
    });
  }

  private enrichAndFilterBundles(items: any[]): any[] {
    const now = this.now();

    const filtered = items.filter((item: any) => {
      if (!item.activatesAt || !item.expiresAt) return true;

      const activatesAtTime = new Date(item.activatesAt).getTime();
      const expiresAtTime = new Date(item.expiresAt).getTime();

      const isCurrentlyActive = now >= activatesAtTime && now <= expiresAtTime;

      const isUpcomingWithin40Days = activatesAtTime > now && activatesAtTime <= now + this.FORTY_DAYS_MS;

      return isCurrentlyActive || isUpcomingWithin40Days;
    });

    return filtered.map((item: any) => ({
      ...item,
      _isAvailable: this.computeIsAvailable(item, now),
      _availabilityLabel: this.computeAvailabilityLabel(item, now),
    }));
  }

  private computeIsAvailable(item: any, now: number): boolean {
    if (!item.activatesAt) return true;
    return new Date(item.activatesAt).getTime() <= now;
  }

  private computeAvailabilityLabel(item: any, now: number): string | null {
    if (!item.activatesAt || this.computeIsAvailable(item, now)) return null;

    const activatesAtDate = new Date(item.activatesAt);
    const nowDate = new Date(now);

    const isSameDay =
      activatesAtDate.getFullYear() === nowDate.getFullYear() &&
      activatesAtDate.getMonth() === nowDate.getMonth() &&
      activatesAtDate.getDate() === nowDate.getDate();

    if (isSameDay) {
      const time = activatesAtDate.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
      return `Disponible à ${time}`;
    }

    const formatted = activatesAtDate.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
    return `Disponible à partir du ${formatted}`;
  }

  getDynamicBtnColor(index: number): string {
    // Si index pair : Couleur primaire, sinon version un peu plus sombre
    return index % 2 === 0 ? 'var(--primary-color)' : 'var(--primary-dark)';
  }

  scrollRight(): void {
    this.scrollContainer.nativeElement.scrollBy({left: 320, behavior: 'smooth'});
  }

  scrollLeft(): void {
    this.scrollContainer.nativeElement.scrollBy({left: -320, behavior: 'smooth'});
  }


  navigateToCategory(categoryId?: string, categorySlug?: string) {
    this.router.navigate(['/product'], {
      queryParams: categorySlug ? {category: categorySlug} : {},
      state: {
        selectedCategoryId: categoryId,
        selectedCategorySlug: categorySlug
      }
    });
  }

  private convertBundleOrderTypes(orderTypes: string[]): TypeOrderEnum[] {
    return orderTypes
      .map((type: string) => {
        if (Object.values(TypeOrderEnum).includes(type as TypeOrderEnum)) {
          return type as TypeOrderEnum;
        }
        return TypeOrderEnum[type as keyof typeof TypeOrderEnum];
      })
      .filter(Boolean);
  }

  openDialogBundle(bundle: any) {
    if (!bundle._isAvailable) return;
    if (this.dialog.openDialogs.length > 0) return;
    const orderTypes = bundle.orderTypes || [];
    const typedOrderTypes = this.convertBundleOrderTypes(orderTypes);
    const firstOrderType = typedOrderTypes.length > 0 ? typedOrderTypes[0] : undefined;

    this.dialog.open(OrderComponent, {
      panelClass: 'custom-dialog',
      autoFocus: false,
      disableClose: true,
      data: {
        selectedBundle: bundle,
        bundleSlug: bundle.slug,
        bundleId: bundle.id,
        preselectedOrderType: firstOrderType,
        allowedOrderTypes: typedOrderTypes,
        context: 'menus'
      }
    });
  }

  getBackgroundImage(index: number): string {
    return this.backgroundImages[index % this.backgroundImages.length];
  }

  getButtonClass(index: number): string {
    return this.buttonClasses[index % this.buttonClasses.length];
  }

  getColorText(index: number) {
    return this.textClasses[index % this.textClasses.length]

  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private setupLayoutListeners() {
    if (!this.scrollContainer?.nativeElement) return;
    const el = this.scrollContainer.nativeElement;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        this.ngZone.run(() => this.refreshLayoutState());
      });
      this.resizeObserver.observe(el);

      if (!this.isScrollListenerAttached) {
        merge(
          fromEvent(window, 'resize'),
          fromEvent(window, 'orientationchange'),
          fromEvent(el, 'scroll')
        ).pipe(
          debounceTime(40),
          takeUntil(this.destroy$)
        ).subscribe(() => {
          this.ngZone.run(() => this.refreshLayoutState());
        });
        this.isScrollListenerAttached = true;
      }
    });

    this.refreshLayoutState();
  }

  private refreshLayoutState() {
    if (!this.scrollContainer?.nativeElement) return;

    const el = this.scrollContainer.nativeElement;
    const tolerance = 5;

    // Calculate Overflow
    const needScroll = el.scrollWidth > el.clientWidth + tolerance;
    this.hasOverflow = needScroll;
    this.showSeeMore = needScroll;

    // Calculate Arrow Visibility
    this.canScrollLeft = el.scrollLeft > 10;
    this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 10;
  }
}
